import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCRefLovProps, RCCRefModalProps, RCCServicesApi, RCCTextArea } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import LovDimensions from "src/components/Lov/Dimensions";
import LovSWOT from "src/components/Lov/SWOT";
import { IMatba004 } from "../interface";
import { schemaAddItemSwot } from "../schemas";

export interface IRefModalAddSwotAnalysis{
	openModal: (row: IMatba004, labelLevel3: string, onCallbackFunction?: Function) => void;
  }

const ModalAddSwotAnalysis: React.ForwardRefRenderFunction<IRefModalAddSwotAnalysis> = (_, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	
	const [selectedLevel3, setSelectedLevel3] = useState<IMatba004>({} as IMatba004);
	const [label, setLabel] = useState<string>();

	const onCallbackFunctionChange = useRef<Function | undefined>(undefined);
	const lovRef = useRef<RCCRefLovProps>(null);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);

	const { 
		handleSubmit : handleSubmitAddOrChange, 
		control: controlAddOrChange,
		register : registerAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange
	} = useForm();

	const openModal = async (row: IMatba004, labelLevel: string, onCallbackFunction?: Function) => {
		onCallbackFunctionChange.current = onCallbackFunction;
		resetAddOrChange();
		setLabel(labelLevel);
		setSelectedLevel3(row);
		modalAddOrChange.current?.openModal();
	};

	const submitAddOrChange = async (obj: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			await schemaAddItemSwot(translate).validate(obj,{
				abortEarly: false
			});

			const {data} = await RCCServicesApi.postRequest({
				method: "Analysis/InsertItemSwot",
				body: {
					...selectedLevel3,
					maa004Descricao: obj.maa004Descricao,
					mes001Id: obj.mes001Id,
					taa011Id: obj.taa011Id
				}
			});

			selectedLevel3.metbs006 = data;
			if(onCallbackFunctionChange)
				onCallbackFunctionChange.current?.(selectedLevel3, false);

			addToast({
				color: "green",
				description: `${translate("Item Swot adicionado com sucesso")}`,
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, translate("Erro ao adicionar item da Swot"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModal
	}));
    
	return (
		<>		
			<Modal
				ref={modalAddOrChange}
				header={{
					title: `${translate("Adicionar Item Swot")}`,
					subtitle: `${label}: ${selectedLevel3.maa004Descricao}`,
					icon: {
						name: "plus"
					}
				}}
				styleInline={ModalSide}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCTextArea 
							name="descriptionAnalysis"
							register={registerAddOrChange}
							label={translate("Item da Análise")}
							maxLength={1000}
							defaultValue={selectedLevel3.maa004Descricao}
							disabled
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<LovSWOT
							control={controlAddOrChange}
							name="mes001Id"
							label="Swot"
							error={errorsAddOrChange.mes001Id}
							required
						/> 
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<LovDimensions 
							name="taa011Id" 
							label={translate("Dimensão padrão")} 
							required
							control={controlAddOrChange}
							ref={lovRef}
							error={errorsAddOrChange.taa011Id}
							extraParametersOnLoad={{
								useConsolidated: false
							}}
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCTextArea 
							name="maa004Descricao"
							register={registerAddOrChange}
							label={translate("Item da Swot")}
							maxLength={1000}
							defaultValue={selectedLevel3.maa004Descricao}
							error={errorsAddOrChange.maa004Descricao}
							required
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalAddSwotAnalysis);
